import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Compass, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function RegisterPage() {
  const { register } = useAuth();
  const { lang, t, toggleLang } = useLanguage();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError(t("auth.password_min"));
      return;
    }
    setLoading(true);
    try {
      await register(form.email, form.password, form.name);
      toast.success(t("auth.welcome"));
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") setError(detail);
      else if (Array.isArray(detail)) setError(detail.map((d) => d.msg).join(", "));
      else setError(t("auth.error_register"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12" style={{ background: "#F9F6F0" }}>
      {/* Lang toggle top-right */}
      <div className="fixed top-4 right-4">
        <button
          onClick={toggleLang}
          className="text-xs px-2 py-1 border border-[#DCD4C6] text-[#9A8F86] hover:border-[#C17F59] hover:text-[#C17F59] transition-all"
          style={{ fontFamily: "'Lora', Georgia, serif", borderRadius: "2px", letterSpacing: "0.06em" }}
          data-testid="lang-toggle-btn"
        >
          {lang === "fr" ? "EN" : "FR"}
        </button>
      </div>
      <div className="w-full max-w-md animate-fade-in-up">
        {/* Logo */}
        <div className="text-center mb-10">
          <Link to="/" className="inline-flex flex-col items-center gap-2" style={{ textDecoration: "none" }}>
            <div className="w-12 h-12 rounded-full border-2 border-[#C17F59] flex items-center justify-center">
              <Compass size={20} color="#C17F59" strokeWidth={1.5} />
            </div>
            <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic", fontWeight: 600, fontSize: "1.6rem", color: "#2B2623" }}>
              Voyage-r
            </span>
          </Link>
          <h1
            className="mt-4 text-3xl"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}
          >
            {t("auth.register_title")}
          </h1>
          <p className="text-sm text-[#5C554E] mt-1 italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            {t("auth.register_subtitle")}
          </p>
        </div>

        <div className="card-surface">
          {error && (
            <div className="mb-5 px-4 py-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700" style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="register-error">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#8B5E34] mb-2" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                {t("auth.name")}
              </label>
              <input
                type="text"
                className="input-field"
                placeholder={t("auth.name_placeholder")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                autoComplete="name"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#8B5E34] mb-2" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                {t("auth.email")}
              </label>
              <input
                type="email"
                className="input-field"
                placeholder={t("auth.email_placeholder")}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                autoComplete="email"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#8B5E34] mb-2" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                {t("auth.password")}
              </label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  className="input-field pr-8"
                  placeholder={t("auth.password_placeholder")}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  autoComplete="new-password"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[#9A8F86] hover:text-[#5C554E]"
                  onClick={() => setShowPwd(!showPwd)}
                >
                  {showPwd ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              className="btn-primary w-full mt-2"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? t("auth.register_loading") : t("auth.register_btn")}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-[#9A8F86] mt-4 italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          {t("auth.tos")}
        </p>
        <p className="text-center text-sm text-[#5C554E] mt-3" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          {t("auth.already_member")}{" "}
          <Link to="/login" className="text-[#C17F59] hover:text-[#A86A46] transition-colors" style={{ fontWeight: 500 }} data-testid="register-login-link">
            {t("auth.login_link")}
          </Link>
        </p>
      </div>
    </div>
  );
}
